import { useCallback, useMemo, useState } from "react";
import * as React from "react";

import NewTouchpointForm from "components/new-touchpoint-form";

import { Patient } from "../services/types/patients.type";

type NewTouchpointProviderProps = {
  children?: React.ReactNode;
};

// Optional values to prefill the New Touchpoint form when it is opened
// programmatically (e.g. from the Alerts report "Create Touchpoint" action).
export type NewTouchpointPrefill = {
  message?: string;
  execution_date?: string;
  assigned_to?: { label: string; value: string };
  // Seed the communication method so the Message box renders on open. Without
  // it, the form's reset() clobbers the child-effect's auto-selected method
  // back to null and the Message field never appears.
  communication_method?: string | number | null;
};

const NewTouchpointContext = React.createContext<any>(null);

export function NewTouchpointProvider({
  children
}: NewTouchpointProviderProps) {
  const [openModal, setOpenModal] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [prefill, setPrefill] = useState<NewTouchpointPrefill | undefined>();
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [initialDocuments, setInitialDocuments] = useState<File[]>([]);
  const onCloseCallbackRef = React.useRef<any>();

  const handleOpen = useCallback(
    (
      patient?: Patient,
      callback?: () => void,
      prefill?: NewTouchpointPrefill
    ) => {
      setPatient(patient || null);
      setPrefill(prefill);
      setOpenModal(true);
      onCloseCallbackRef.current = callback;
    },
    []
  );

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setPatient(null);
    setPrefill(undefined);
    setInitialDocuments([]);
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
    }
  }, []);

  const value = useMemo(
    () => ({
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setInitialDocuments
    }),
    [handleOpen, selectedFiles, setSelectedFiles, setInitialDocuments]
  );

  return (
    <NewTouchpointContext.Provider value={value}>
      {children}
      <NewTouchpointForm
        isOpen={openModal}
        onClose={handleClose}
        patient={patient}
        prefill={prefill}
        selectedFiles={selectedFiles}
        initialDocuments={initialDocuments}
      />
    </NewTouchpointContext.Provider>
  );
}

export default function useNewTouchpoint() {
  const context = React.useContext(NewTouchpointContext);
  if (!context) {
    throw new Error(
      `useNewTouchpoint must be used within a NewTouchpointProvider`
    );
  }
  const { handleOpen, selectedFiles, setSelectedFiles, setInitialDocuments } =
    context;
  return {
    handleOpen,
    selectedFiles,
    setSelectedFiles,
    setInitialDocuments
  };
}
